import { Chip, useDisclosure } from "@heroui/react";
import { useState, useEffect, useRef, useCallback } from "react";
import { EditAgreementModal } from "../modals/agreements/editAgreementModal";
import { Agreement } from "@/api/agreements";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { ContractStatus } from "@/api/analytics";

export function FinanceRentedOfficeChip({
  rentedOfficeId,
  fallbackCallback,
  callback,
  contractStatus,
  displayName,
}: {
  rentedOfficeId: number;
  fallbackCallback: () => void;
  callback: (item: Agreement) => void;
  contractStatus: ContractStatus;
  displayName: string;
}) {
  const [item, setItem] = useState<Agreement | null>(null);
  const items = useAppSelector((state) => state.handbooks.agreements);

  useEffect(() => {
    if (rentedOfficeId === -1) {
      setItem(null);
    } else {
      setItem(items.find((el) => el.id === rentedOfficeId) || null);
    }
  }, [rentedOfficeId, items]);

  const getColor = (contractStatus: ContractStatus) => {
    return contractStatus == ContractStatus.LESS_THAN_WEEK
      ? "danger"
      : contractStatus == ContractStatus.MORE_THAN_WEEK
        ? "warning"
        : "primary";
  };

  return (
    <>
      {rentedOfficeId === -1 && (
        <Chip variant="dot" color="danger">
          удалено
        </Chip>
      )}
      {rentedOfficeId !== -1 && item !== null ? (
        <Chip
          className="cursor-pointer"
          onClick={() => callback(item)}
          variant="dot"
          color={getColor(contractStatus)}>
          {displayName}
        </Chip>
      ) : (
        rentedOfficeId !== -1 && (
          <Chip
            variant="dot"
            color="default"
            className="cursor-pointer"
            onClick={fallbackCallback}>
            {displayName}
          </Chip>
        )
      )}
    </>
  );
}

export function RentedOfficeChip({
  displayName,
  rentedOfficeId,
  contractStatus,
  fallbackCallback = () => {},
  openCallback = () => {},
}: {
  displayName: string;
  rentedOfficeId: number;
  contractStatus: ContractStatus;
  fallbackCallback?: () => void;
  openCallback?: () => void;
}) {
  const [item, setItem] = useState<Agreement | null>(null);
  const disclosure = useDisclosure();
  const items = useAppSelector((state) => state.handbooks.agreements);

  useEffect(() => {
    if (rentedOfficeId === -1) {
      setItem(null);
    } else {
      setItem(items.find((el) => el.id === rentedOfficeId) || null);
    }
  }, [rentedOfficeId, items]);

  const prevIsOpenRef = useRef(disclosure.isOpen);

  useEffect(() => {
    if (prevIsOpenRef.current && !disclosure.isOpen) {
      openCallback();
      loadHandbooks({ handbooks: ["agreements"] });
    }

    prevIsOpenRef.current = disclosure.isOpen;
  }, [disclosure.isOpen, openCallback]);

  const onClick = useCallback(() => {
    disclosure.onOpen();
  }, [disclosure]);

  const getColor = (contractStatus: ContractStatus) => {
    return contractStatus == ContractStatus.LESS_THAN_WEEK
      ? "danger"
      : contractStatus == ContractStatus.MORE_THAN_WEEK
        ? "warning"
        : "primary";
  };

  return (
    <>
      <EditAgreementModal disclosure={disclosure} office={item} />

      {rentedOfficeId === -1 && (
        <Chip variant="dot" color="danger">
          удалено
        </Chip>
      )}
      {rentedOfficeId !== -1 && item !== null ? (
        <Chip
          className="cursor-pointer"
          onClick={onClick}
          variant="dot"
          color={getColor(contractStatus)}>
          {displayName}
        </Chip>
      ) : (
        rentedOfficeId !== -1 && (
          <Chip
            variant="dot"
            color="default"
            className="cursor-pointer"
            onClick={fallbackCallback}>
            {displayName}
          </Chip>
        )
      )}
    </>
  );
}
